import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, X, RefreshCw, Repeat, Trash2, Plus } from "lucide-react";
import "./architect.css";

const ROOM_BG_DESKTOP = "/architect-desktop.png";
const ROOM_BG_MOBILE = "/architect-mobile.png";

// Baked stand top surface sits at ~68.8% of the 9:16 image height.
// Cake column is anchored there via CSS. Layer height in px is derived from
// scene height: 1 cm ≈ 1.3% of viewport height (so a 3 cm biscuit ≈ 3.9%).
const CM_TO_VH = 2.1;
const HEIGHT_WARN = 25;
const HEIGHT_MAX = 30;

// ---------- helpers ----------
function fmtWeight(g) {
  if (g >= 1000) return `≈${(g / 1000).toFixed(1)} кг`;
  return `≈${Math.round(g)} г`;
}
function totalHeightCm(layers) {
  return layers.reduce((s, l) => s + (Number(l.thickness_cm) || 0), 0);
}
function approxWeight(layers, catalog) {
  if (!catalog) return 0;
  const density = {
    biscuit: 0.48, cream: 0.85, ganache: 1.05, mousse: 0.55,
    confit: 1.15, compote: 1.10, cremeux: 1.05, caramel: 1.25,
    crunch: 0.90, frozen_disc: 0.95,
  };
  const r = catalog.cake_diameter_cm / 2;
  const area = Math.PI * r * r;
  let g = 0;
  const inclById = Object.fromEntries((catalog.inclusions || []).map(i => [i.id, i]));
  for (const l of layers) {
    g += area * (l.thickness_cm || 0) * (density[l.kind] || 0.9);
    for (const iid of (l.inclusions || [])) {
      g += (inclById[iid]?.grams_per_layer || 0);
    }
    if (l.soak_id) {
      const s = (catalog.soaks || []).find(x => x.id === l.soak_id);
      g += s?.grams || 0;
    }
  }
  return g;
}

// ---------- Main component ----------
export default function Architect() {
  const nav = useNavigate();
  const { id: urlId } = useParams();
  const { user } = useAuth();

  const [catalog, setCatalog] = useState(null);
  const [balance, setBalance] = useState(null);
  const [construction, setConstruction] = useState(null); // {id, name, layers[], status}
  const [selectedLayerId, setSelectedLayerId] = useState(null);

  // Progressive picker state
  const [picker, setPicker] = useState(null); // {category, subtypeId, thickness, inclusions[], damMode}
  const [soakOverlay, setSoakOverlay] = useState(false);
  const [damChoice, setDamChoice] = useState(null); // {subtypeId, flavorId, kind, thickness_cm, placement}

  // ------- initial load -------
  useEffect(() => {
    (async () => {
      try {
        const [c, b, cur] = await Promise.all([
          api.get("/architect/catalog"),
          api.get("/account/architect/balance"),
          api.get("/architect/current"),
        ]);
        setCatalog(c.data);
        setBalance(b.data);
        if (urlId) {
          const { data } = await api.get(`/constructions/${urlId}`);
          setConstruction(data);
        } else if (cur.data) {
          setConstruction(cur.data);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [urlId]);

  const layers = useMemo(() => (construction?.layers || []).slice().sort((a, b) => a.order - b.order), [construction]);
  const heightCm = totalHeightCm(layers);
  const weight = approxWeight(layers, catalog);
  const noLayers = layers.length === 0;

  // ------- create / persist -------
  const persist = useCallback(async (newLayers, name) => {
    const payload = {
      name: name || construction?.name || "Мой десерт",
      layers: newLayers.map((l, i) => ({
        component_id: l.component_id || l.flavor_id,
        thickness_cm: l.thickness_cm,
        order: i,
        kind: l.kind,
        subtype_id: l.subtype_id,
        flavor_id: l.flavor_id,
        dam_mode: l.dam_mode,
        soak_id: l.soak_id,
        inclusions: l.inclusions || [],
        state: l.state,
        color: l.color,
        label: l.label,
      })),
    };
    try {
      if (construction?.id) {
        const { data } = await api.put(`/constructions/${construction.id}`, payload);
        setConstruction(data);
        return data;
      } else {
        const { data } = await api.post("/constructions", payload);
        setConstruction(data);
        nav(`/architect/${data.id}`, { replace: true });
        return data;
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "draft_exists" && detail.existing_draft_id) {
        // Load the existing draft — server enforces one draft rule
        const { data } = await api.get(`/constructions/${detail.existing_draft_id}`);
        setConstruction(data);
        toast("Продолжаем текущую сборку");
        nav(`/architect/${detail.existing_draft_id}`, { replace: true });
        return data;
      }
      toast.error(formatApiError(e));
      throw e;
    }
  }, [construction, nav]);

  const startConstruction = useCallback(async () => {
    // Explicit "Начать сборку" — creates an empty draft, then user selects first biscuit
    await persist([], "Мой десерт");
  }, [persist]);

  const restartConstruction = useCallback(async () => {
    if (!construction?.id) return;
    try {
      await api.delete(`/constructions/${construction.id}`);
      setConstruction(null);
      setSelectedLayerId(null);
      setPicker(null);
      nav("/architect", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [construction, nav]);

  // ------- picker helpers -------
  const openCategory = useCallback((cat) => {
    setPicker({ category: cat, subtypeId: null, thickness: 1.5, inclusions: [], damMode: null });
  }, []);
  const closePicker = useCallback(() => { setPicker(null); }, []);

  const addLayerFromFlavor = useCallback(async (flavor, opts = {}) => {
    // opts: {thickness, inclusions, damMode, subtypeId}
    // Validate max height before adding
    const nextHeight = heightCm + (opts.thickness || flavor.default_thickness || 1.5);
    if (nextHeight > HEIGHT_MAX) {
      toast.error("Максимальная высота торта 30 см достигнута.");
      return;
    }
    if (nextHeight > HEIGHT_WARN) {
      toast("Высота торта больше 25 см. Это высокая конструкция — учитывайте её устойчивость при сборке и переноске.");
    }
    const layer = {
      id: `tmp-${Date.now()}`,
      kind: flavor.kind || opts.kind,
      subtype_id: opts.subtypeId || flavor.subtype_id || null,
      flavor_id: flavor.id,
      thickness_cm: opts.thickness || 1.5,
      dam_mode: opts.damMode || null,
      inclusions: opts.inclusions || [],
      state: flavor.state || null,
      color: flavor.color,
      label: flavor.name,
      order: layers.length,
    };
    const updated = [...layers, layer];
    const saved = await persist(updated);
    // Do NOT auto-select the just-added layer: keep the "Что добавим дальше?" panel
    // visible so the user can freely continue composing. The newest layer's label
    // still appears via the activeLayer fallback (last layer).
    setPicker(null);
    setDamChoice(null);
  }, [heightCm, layers, persist]);

  const selectFlavor = useCallback((subtype, flavor) => {
    // Special: filling with dam_recommended or dam_required
    if ((flavor.placement === "dam_recommended" || flavor.placement === "dam_required")) {
      setDamChoice({
        subtype, flavor,
        thickness: picker.thickness,
        placement: flavor.placement,
      });
      return;
    }
    addLayerFromFlavor(flavor, {
      subtypeId: subtype.id,
      thickness: picker.thickness,
    });
  }, [picker, addLayerFromFlavor]);

  const confirmDamChoice = useCallback((mode) => {
    // mode: "standalone" | "in_dam"
    if (!damChoice) return;
    addLayerFromFlavor(damChoice.flavor, {
      subtypeId: damChoice.subtype.id,
      thickness: damChoice.thickness,
      damMode: mode,
    });
  }, [damChoice, addLayerFromFlavor]);

  const addBiscuit = useCallback(async (biscuit, thickness) => {
    await addLayerFromFlavor(biscuit, { thickness, kind: "biscuit" });
  }, [addLayerFromFlavor]);

  // Soak — attach to topmost biscuit that has NO layer above
  const applySoak = useCallback(async (soak) => {
    // Find selected biscuit OR topmost biscuit that has no non-biscuit layers on top
    const target = (() => {
      if (selectedLayerId) {
        const s = layers.find(l => l.id === selectedLayerId);
        if (s && s.kind === "biscuit") return s;
      }
      // topmost biscuit
      const biscuits = layers.filter(l => l.kind === "biscuit");
      if (biscuits.length === 0) return null;
      return biscuits[biscuits.length - 1];
    })();
    if (!target) { toast.error("Пропитка применяется к бисквиту"); return; }
    // Rule: no soak retro-edit if another layer already above
    const idx = layers.findIndex(l => l.id === target.id);
    if (idx < layers.length - 1) {
      toast("Уберите верхние слои, чтобы изменить пропитку этого бисквита");
      return;
    }
    setSoakOverlay(true);
    const newLayers = layers.map(l => l.id === target.id ? { ...l, soak_id: soak.id } : l);
    setTimeout(() => setSoakOverlay(false), 1400);
    await persist(newLayers);
    toast.success(`Пропитка: ${soak.name.replace("Пропитка", "").toLowerCase().trim() || soak.name} · ${soak.grams} г`);
    setPicker(null);
  }, [selectedLayerId, layers, persist]);

  // Layer actions: replace/repeat/remove
  const removeLayer = useCallback(async (lid) => {
    const filtered = layers.filter(l => l.id !== lid).map((l, i) => ({ ...l, order: i }));
    setSelectedLayerId(null);
    await persist(filtered);
  }, [layers, persist]);

  const repeatLayer = useCallback(async (l) => {
    // For complex dam-filling: don't blindly clone — spec §20
    if (l.dam_mode === "in_dam") {
      // Open picker to rebuild the block
      openCategory("Начинка");
      setSelectedLayerId(null);
      toast("Соберите новый уровень заново");
      return;
    }
    if ((heightCm + l.thickness_cm) > HEIGHT_MAX) {
      toast.error("Максимальная высота торта 30 см достигнута.");
      return;
    }
    const copy = { ...l, id: `tmp-${Date.now()}`, order: layers.length };
    await persist([...layers, copy]);
  }, [layers, heightCm, persist, openCategory]);

  const replaceLayer = useCallback((l) => {
    // Open the same category picker for replacement
    const catByKind = {
      biscuit: "Бисквит", cream: "Крем", ganache: "Крем",
      mousse: "Мусс", confit: "Начинка", compote: "Начинка",
      cremeux: "Начинка", caramel: "Начинка", crunch: "Хруст",
    };
    const cat = catByKind[l.kind] || "Крем";
    setPicker({
      category: cat, subtypeId: l.subtype_id || null,
      thickness: l.thickness_cm, inclusions: l.inclusions || [], damMode: l.dam_mode,
      replaceLayerId: l.id,
    });
  }, []);

  const performReplace = useCallback(async (subtype, flavor) => {
    if (!picker?.replaceLayerId) return;
    const newLayers = layers.map(l =>
      l.id === picker.replaceLayerId
        ? {
            ...l,
            flavor_id: flavor.id, kind: flavor.kind || l.kind,
            subtype_id: subtype.id, color: flavor.color, label: flavor.name,
            state: flavor.state || l.state,
          }
        : l
    );
    await persist(newLayers);
    setPicker(null);
  }, [picker, layers, persist]);

  const toggleInclusion = useCallback(async (layerId, inclusionId) => {
    const newLayers = layers.map(l => {
      if (l.id !== layerId) return l;
      const has = (l.inclusions || []).includes(inclusionId);
      return { ...l, inclusions: has
        ? l.inclusions.filter(x => x !== inclusionId)
        : [...(l.inclusions || []), inclusionId]
      };
    });
    await persist(newLayers);
  }, [layers, persist]);

  const setLayerThickness = useCallback(async (layerId, thickness) => {
    // Enforce max height
    const cur = layers.find(l => l.id === layerId);
    if (!cur) return;
    const nextHeight = heightCm - cur.thickness_cm + thickness;
    if (nextHeight > HEIGHT_MAX) {
      toast.error("Максимальная высота торта 30 см достигнута.");
      return;
    }
    const newLayers = layers.map(l => l.id === layerId ? { ...l, thickness_cm: thickness } : l);
    await persist(newLayers);
  }, [layers, heightCm, persist]);

  const selectedLayer = layers.find(l => l.id === selectedLayerId) || null;
  const activeLayer = selectedLayer || (layers.length > 0 ? layers[layers.length - 1] : null);

  return (
    <div className="architect-scene" data-testid="architect-scene">
      <picture className="room-bg" aria-hidden>
        <source media="(max-width: 899px)" srcSet={ROOM_BG_MOBILE} />
        <source media="(min-width: 900px)" srcSet={ROOM_BG_DESKTOP} />
        <img src={ROOM_BG_MOBILE} alt="" draggable={false} data-testid="architect-room-bg" />
      </picture>

      {/* Top bar */}
      <div className="arch-topbar">
        <button
          onClick={() => nav("/hall")}
          className="arch-icon-btn"
          data-testid="architect-back-button"
          aria-label="Назад в главный зал"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 data-testid="architect-title">Архитектор десертов</h1>
        <div className="arch-credit-chip" data-testid="architect-credit-chip">
          {balance?.unlimited
            ? "∞ сборок"
            : `${balance?.credits_remaining ?? "…"} сборок`}
        </div>
      </div>

      {/* Fixed cake stand + cake column — the anchor NEVER moves.
          The stand is BAKED into the background image at ~68.8% height,
          so we don't draw one in CSS — we align the column bottom to that point. */}
      <div className="stage-anchor" data-testid="stage-anchor">
        <div
          className="stage-cake-column"
          style={(() => {
            // Smooth scale curve — cake stays fully visible from 0 to 30 cm.
            // baseline height (cm) that fits at 100% scale
            const BASE_H = 12;
            const scale = heightCm <= BASE_H ? 1 : Math.max(0.42, BASE_H / heightCm);
            return { transform: `scale(${scale})` };
          })()}
          data-testid="cake-column"
        >
          {layers.map((l, i) => (
            <CakeLayerVisual
              key={l.id}
              layer={l}
              first={i === 0}
              selected={selectedLayerId === l.id}
              catalog={catalog}
              onSelect={() => setSelectedLayerId(l.id === selectedLayerId ? null : l.id)}
            />
          ))}
          {soakOverlay && (
            <div className="soak-brush" data-testid="soak-brush" aria-hidden>
              <svg viewBox="0 0 84 44" xmlns="http://www.w3.org/2000/svg">
                {/* wooden handle */}
                <rect x="52" y="14" width="30" height="14" rx="3" fill="url(#handleGrad)" />
                {/* metal ferrule */}
                <rect x="42" y="10" width="14" height="22" rx="1.5" fill="#B7A283" />
                <rect x="42" y="10" width="14" height="22" rx="1.5" fill="none" stroke="#7C6849" strokeWidth="0.8" />
                {/* soft bristles — clumped fibers */}
                <g fill="none" stroke="#D9C79E" strokeWidth="1.6" strokeLinecap="round" opacity="0.95">
                  <path d="M42 12 Q22 12 4 16" />
                  <path d="M42 16 Q22 18 4 22" />
                  <path d="M42 20 Q22 22 4 26" />
                  <path d="M42 24 Q22 26 4 28" />
                  <path d="M42 28 Q22 28 4 30" />
                  <path d="M42 32 Q22 32 4 32" />
                </g>
                {/* wet tips (slightly darker) */}
                <g fill="none" stroke="#8B6B3E" strokeWidth="1.2" strokeLinecap="round" opacity="0.65">
                  <path d="M22 15 Q14 15 6 17" />
                  <path d="M22 23 Q14 24 6 25" />
                  <path d="M22 30 Q14 31 6 32" />
                </g>
                <defs>
                  <linearGradient id="handleGrad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0" stopColor="#8B5E32" />
                    <stop offset="1" stopColor="#4E321A" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Active-layer label — anchored under topbar, never overlaps title */}
      {activeLayer && (
        <div className="active-label" data-testid="active-layer-label">
          <div className="name">{activeLayer.label}</div>
          <div className="measure">
            {(activeLayer.kind === "biscuit" || activeLayer.kind === "cream" || activeLayer.kind === "ganache" || activeLayer.kind === "mousse")
              ? `${activeLayer.thickness_cm.toString().replace(".", ",")} см`
              : activeLayer.kind === "crunch"
                ? "тонкий слой"
                : `${activeLayer.thickness_cm.toString().replace(".", ",")} см`}
          </div>
        </div>
      )}

      {/* Live info bar below the stand */}
      {catalog && layers.length > 0 && (
        <div className="arch-liveinfo" data-testid="architect-liveinfo">
          Ø{catalog.cake_diameter_cm} · {heightCm.toFixed(1).replace(".", ",")} см · {fmtWeight(weight)}
        </div>
      )}

      {/* Bottom glass panel */}
      <div className="arch-glass" data-testid="architect-panel">
        {!construction && (
          <StartPanel onStart={startConstruction} />
        )}
        {construction && noLayers && catalog && (
          <FirstBiscuitPanel
            catalog={catalog}
            onPick={(b, thickness) => addBiscuit(b, thickness)}
          />
        )}
        {construction && !noLayers && !picker && !damChoice && !selectedLayer && (
          <NextCategoryPanel
            onPick={openCategory}
            onContinue={() => setPicker({ category: "Бисквит", subtypeId: null, thickness: 1.5 })}
            hasDraft={!noLayers}
          />
        )}
        {construction && selectedLayer && !picker && (
          <SelectedLayerPanel
            layer={selectedLayer}
            catalog={catalog}
            onClose={() => setSelectedLayerId(null)}
            onReplace={() => replaceLayer(selectedLayer)}
            onRepeat={() => repeatLayer(selectedLayer)}
            onRemove={() => removeLayer(selectedLayer.id)}
            onThickness={(t) => setLayerThickness(selectedLayer.id, t)}
            onToggleInclusion={(id) => toggleInclusion(selectedLayer.id, id)}
            stops={catalog?.thickness_stops || []}
          />
        )}
        {picker && catalog && (
          <CategoryPicker
            picker={picker}
            catalog={catalog}
            onClose={closePicker}
            onSubtype={(sid) => setPicker(p => ({ ...p, subtypeId: sid }))}
            onThickness={(t) => setPicker(p => ({ ...p, thickness: t }))}
            onToggleInclusion={(id) => setPicker(p => ({
              ...p,
              inclusions: (p.inclusions || []).includes(id)
                ? p.inclusions.filter(x => x !== id)
                : [...(p.inclusions || []), id],
            }))}
            onSelectFlavor={(subtype, flavor) => {
              if (picker.replaceLayerId) return performReplace(subtype, flavor);
              return selectFlavor(subtype, flavor);
            }}
            onApplySoak={(soak) => applySoak(soak)}
          />
        )}
        {damChoice && (
          <DamChoicePanel
            damChoice={damChoice}
            onCancel={() => setDamChoice(null)}
            onChoose={confirmDamChoice}
          />
        )}
      </div>
    </div>
  );
}

// ---------- Sub components ----------

function CakeLayerVisual({ layer, first, selected, catalog, onSelect }) {
  // Per-layer height in vh — proportional to thickness_cm.
  // Crunch is a thin visual band regardless of technical thickness.
  const isCrunch = layer.kind === "crunch";
  const vh = isCrunch ? 0.9 : Math.max(1.0, (layer.thickness_cm || 1) * CM_TO_VH);
  const isCream = layer.kind === "cream" || layer.kind === "ganache";
  const inclById = Object.fromEntries((catalog?.inclusions || []).map(i => [i.id, i]));
  const inclusionIds = layer.inclusions || [];
  return (
    <div
      className={`cake-layer kind-${layer.kind} ${selected ? "selected" : ""} ${first ? "first" : ""} ${layer.dam_mode === "in_dam" ? "dam" : ""}`}
      data-testid={`cake-layer-${layer.id}`}
      onClick={onSelect}
      style={{ "--core": layer.color }}
    >
      <div
        className="layer-side"
        style={{
          height: `${vh}vh`,
          minHeight: 10,
          background: layer.dam_mode === "in_dam" ? "#FBF3DD" : layer.color,
        }}
      >
        {layer.dam_mode === "in_dam" && (
          <div className="filling-core" style={{ background: layer.color }} />
        )}
        {isCream && inclusionIds.length > 0 && (
          <div className="inclusions">
            {inclusionIds.flatMap((iid, i) => {
              const c = inclById[iid]?.color || "#B32F2F";
              // 4 dots per inclusion, staggered
              return [0.25, 0.55, 0.75].map((y, j) => (
                <span
                  key={`${iid}-${i}-${j}`}
                  className="inclusion-dot"
                  style={{
                    left: `${8 + (i * 26) + (j * 9)}%`,
                    top: `${y * 100}%`,
                    background: c,
                  }}
                />
              ));
            })}
          </div>
        )}
      </div>
      <div className="layer-top" style={{ background: layer.color }} />
    </div>
  );
}

function StartPanel({ onStart }) {
  return (
    <div>
      <p style={{ textAlign: "center", opacity: 0.85, fontSize: 13, marginBottom: 12 }}>
        Соберите свой торт по слоям
      </p>
      <button className="arch-primary-btn" onClick={onStart} data-testid="architect-start-btn">
        Начать сборку
      </button>
    </div>
  );
}

function FirstBiscuitPanel({ catalog, onPick }) {
  const [thickness, setThickness] = useState(2.0);
  return (
    <div data-testid="first-biscuit-panel">
      <h2>Выберите основу — бисквит</h2>
      <div className="arch-flavor-scroll">
        {catalog.biscuits.map(b => (
          <button
            key={b.id}
            className="arch-flavor-card"
            data-testid={`biscuit-card-${b.id}`}
            onClick={() => onPick(b, thickness)}
          >
            <div className={`arch-flavor-swatch kind-biscuit`} style={{ background: b.color }} />
            <div className="lbl">{b.name}</div>
          </button>
        ))}
      </div>
      <p style={{ textAlign: "center", opacity: 0.75, fontSize: 12, marginTop: 12 }}>Толщина</p>
      <div className="arch-thickness-pills">
        {catalog.thickness_stops.map(t => (
          <button
            key={t}
            className={`arch-thickness-pill ${thickness === t ? "active" : ""}`}
            onClick={() => setThickness(t)}
            data-testid={`thickness-pill-${t}`}
          >
            {t} см
          </button>
        ))}
      </div>
    </div>
  );
}

function NextCategoryPanel({ onPick }) {
  const cats = [
    { id: "Бисквит", label: "Бисквит" },
    { id: "Крем", label: "Крем" },
    { id: "Начинка", label: "Начинка" },
    { id: "Мусс", label: "Мусс" },
    { id: "Хруст", label: "Хруст" },
    { id: "Пропитать", label: "Пропитать" },
  ];
  return (
    <div data-testid="next-category-panel">
      <h2>Что добавим дальше?</h2>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
        {cats.map(c => (
          <button
            key={c.id}
            className="arch-cat-chip"
            data-testid={`cat-chip-${c.id}`}
            onClick={() => onPick(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function CategoryPicker({ picker, catalog, onClose, onSubtype, onThickness, onToggleInclusion, onSelectFlavor, onApplySoak }) {
  const { category, subtypeId, thickness, inclusions } = picker;
  // Resolve subtypes/flavor lists per category
  const subtypes = useMemo(() => {
    switch (category) {
      case "Бисквит": return [{ id: "b", name: "", flavors: catalog.biscuits.map(f => ({ ...f, kind: "biscuit" })) }];
      case "Крем":    return catalog.cream_subtypes;
      case "Начинка": return catalog.filling_subtypes;
      case "Мусс":    return [{ id: "m", name: "", flavors: catalog.mousse }];
      case "Хруст":   return [{ id: "cr", name: "", flavors: catalog.crunch }];
      case "Пропитать": return null;
      default: return [];
    }
  }, [category, catalog]);
  const isBiscuit = category === "Бисквит";
  const isCream = category === "Крем";
  const isSoak = category === "Пропитать";
  const supportsThickness = isBiscuit || isCream;
  const supportsInclusions = isCream;

  const currentSubtype = subtypeId ? subtypes.find(s => s.id === subtypeId) : (subtypes && subtypes.length === 1 ? subtypes[0] : null);

  return (
    <div data-testid={`picker-${category}`}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h2 style={{ margin: 0 }}>
          {picker.replaceLayerId ? "Заменить слой" : (isSoak ? "Выберите пропитку" : `Выберите ${category.toLowerCase()}`)}
        </h2>
        <button onClick={onClose} className="arch-icon-btn" style={{ width: 34, height: 34 }} data-testid="picker-close">
          <X size={16} />
        </button>
      </div>

      {isSoak && (
        <div className="arch-flavor-scroll">
          {catalog.soaks.map(s => (
            <button
              key={s.id}
              className="arch-flavor-card"
              data-testid={`soak-card-${s.id}`}
              onClick={() => onApplySoak(s)}
            >
              <div className="arch-flavor-swatch" style={{ background: "#7B5227" }} />
              <div className="lbl">{s.name}</div>
            </button>
          ))}
        </div>
      )}

      {!isSoak && subtypes && subtypes.length > 1 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", marginBottom: 10 }}>
          {subtypes.map(s => (
            <button
              key={s.id}
              className={`arch-cat-chip ${currentSubtype?.id === s.id ? "active" : ""}`}
              onClick={() => onSubtype(s.id)}
              data-testid={`subtype-chip-${s.id}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {!isSoak && currentSubtype && (
        <div className="arch-flavor-scroll" data-testid="flavor-scroll">
          {currentSubtype.flavors.map(f => (
            <button
              key={f.id}
              className="arch-flavor-card"
              data-testid={`flavor-card-${f.id}`}
              onClick={() => onSelectFlavor(currentSubtype, f)}
            >
              <div className={`arch-flavor-swatch kind-${f.kind || "cream"}`} style={{ background: f.color }} />
              <div className="lbl">{f.name}</div>
            </button>
          ))}
        </div>
      )}

      {supportsThickness && (
        <>
          <p style={{ textAlign: "center", opacity: 0.75, fontSize: 12, marginTop: 12 }}>Толщина</p>
          <div className="arch-thickness-pills">
            {catalog.thickness_stops.map(t => (
              <button
                key={t}
                className={`arch-thickness-pill ${thickness === t ? "active" : ""}`}
                onClick={() => onThickness(t)}
                data-testid={`thickness-pill-${t}`}
              >
                {t} см
              </button>
            ))}
          </div>
        </>
      )}

      {supportsInclusions && (
        <>
          <p style={{ textAlign: "center", opacity: 0.75, fontSize: 12, marginTop: 12 }}>
            Кусочки в креме (по желанию)
          </p>
          <div className="arch-flavor-scroll">
            {catalog.inclusions.map(i => {
              const active = (inclusions || []).includes(i.id);
              return (
                <button
                  key={i.id}
                  className={`arch-flavor-card ${active ? "active" : ""}`}
                  data-testid={`inclusion-card-${i.id}`}
                  onClick={() => onToggleInclusion(i.id)}
                >
                  <div className="arch-flavor-swatch" style={{ background: i.color }} />
                  <div className="lbl">{i.name}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function DamChoicePanel({ damChoice, onCancel, onChoose }) {
  const isRequired = damChoice.placement === "dam_required";
  return (
    <div data-testid="dam-choice-panel">
      <h2>Как разместить начинку?</h2>
      <p style={{ textAlign: "center", opacity: 0.85, fontSize: 12, marginBottom: 10 }}>
        {damChoice.flavor.name}
        {isRequired && " · требует кремовый бортик"}
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          className="arch-cat-chip"
          data-testid="dam-choice-in-dam"
          onClick={() => onChoose("in_dam")}
        >
          В кремовом бортике
        </button>
        {!isRequired && (
          <button
            className="arch-cat-chip"
            data-testid="dam-choice-standalone"
            onClick={() => onChoose("standalone")}
          >
            Отдельным слоем
          </button>
        )}
        <button className="arch-cat-chip" onClick={onCancel} data-testid="dam-choice-cancel">
          Отмена
        </button>
      </div>
    </div>
  );
}

function SelectedLayerPanel({ layer, catalog, onClose, onReplace, onRepeat, onRemove, onThickness, onToggleInclusion, stops }) {
  const isCream = layer.kind === "cream" || layer.kind === "ganache";
  const supportsThickness = ["biscuit", "cream", "ganache"].includes(layer.kind);
  return (
    <div data-testid="selected-layer-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <h2 style={{ margin: 0 }}>{layer.label}</h2>
        <button onClick={onClose} className="arch-icon-btn" style={{ width: 34, height: 34 }} data-testid="selected-close">
          <X size={16} />
        </button>
      </div>
      {supportsThickness && (
        <>
          <p style={{ textAlign: "center", opacity: 0.75, fontSize: 12 }}>Толщина</p>
          <div className="arch-thickness-pills">
            {stops.map(t => (
              <button
                key={t}
                className={`arch-thickness-pill ${layer.thickness_cm === t ? "active" : ""}`}
                onClick={() => onThickness(t)}
                data-testid={`sel-thickness-${t}`}
              >
                {t} см
              </button>
            ))}
          </div>
        </>
      )}
      {isCream && catalog && (
        <>
          <p style={{ textAlign: "center", opacity: 0.75, fontSize: 12, marginTop: 12 }}>
            Кусочки в креме
          </p>
          <div className="arch-flavor-scroll">
            {catalog.inclusions.map(i => {
              const active = (layer.inclusions || []).includes(i.id);
              return (
                <button
                  key={i.id}
                  className={`arch-flavor-card ${active ? "active" : ""}`}
                  data-testid={`sel-inclusion-${i.id}`}
                  onClick={() => onToggleInclusion(i.id)}
                >
                  <div className="arch-flavor-swatch" style={{ background: i.color }} />
                  <div className="lbl">{i.name}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "center", flexWrap: "wrap" }}>
        <button className="arch-cat-chip" onClick={onReplace} data-testid="sel-replace">
          <RefreshCw size={14} /> Заменить
        </button>
        <button className="arch-cat-chip" onClick={onRepeat} data-testid="sel-repeat">
          <Repeat size={14} /> Повторить
        </button>
        <button className="arch-cat-chip" onClick={onRemove} data-testid="sel-remove">
          <Trash2 size={14} /> Убрать
        </button>
      </div>
    </div>
  );
}
